// "Ask BGO" for the standalone /rfc app. Takes the rfc auth token as a
// prop and attaches it per-request (this app has no shared axios default header).
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, X, Send, Bot, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What are the key highlights?",
  "Summarize this proposal",
  "What are the main numbers and costs?",
];

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3.5 py-2.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-[#6f8592] animate-bounce"
          style={{ animationDelay: `${i * 0.12}s` }}
        />
      ))}
    </div>
  );
}

function boldSpans(text, keyPrefix) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4
      ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
      : <span key={`${keyPrefix}-${i}`}>{part}</span>
  );
}

function tableCells(line) {
  const cells = line.split("|").map((c) => c.trim());
  if (cells[0] === "") cells.shift();
  if (cells[cells.length - 1] === "") cells.pop();
  return cells;
}

const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isSeparatorRow = (line) => isTableRow(line) && /^[\s|:-]+$/.test(line);

function MarkdownLite({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === "") {
      i++;
      continue;
    }
    if (isTableRow(lines[i]) && isSeparatorRow(lines[i + 1] || "")) {
      const rowIndex = i;
      const header = tableCells(lines[i]);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) {
        rows.push(tableCells(lines[j]));
        j++;
      }
      blocks.push(
        <div key={rowIndex} className="overflow-x-auto rounded-lg border border-[#d7dee1]">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-[#dcedf2]">
                {header.map((c, ci) => (
                  <th key={ci} className="border-b border-[#c7d5db] px-2 py-1 text-left font-bold text-[#0b5f78]">
                    {boldSpans(c, `h${rowIndex}-${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-white" : "bg-[#fbfcfd]"}>
                  {r.map((c, ci) => (
                    <td key={ci} className="border-b border-[#eef2f4] px-2 py-1 align-top">
                      {boldSpans(c, `r${rowIndex}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    }
    blocks.push(<p key={i} className="m-0">{boldSpans(lines[i], `p${i}`)}</p>);
    i++;
  }
  return <>{blocks}</>;
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex items-end gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
      {!isUser && (
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0f7fa1] to-[#0b5f78]">
          <Bot className="h-3.5 w-3.5 text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed ${
          isUser
            ? "rounded-br-sm whitespace-pre-wrap bg-[#0f7fa1] text-white"
            : "space-y-1.5 rounded-bl-sm bg-[#eef2f4] text-[#0d1b26]"
        }`}
      >
        {isUser ? content : <MarkdownLite text={content} />}
      </div>
    </div>
  );
}

export default function RfcCopilot({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { role: "user", content: message }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/rfc/chat`,
        { message, history },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong reaching Ask BGO. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div style={{ fontFamily: "'Work Sans', -apple-system, sans-serif" }}>
      {open && (
        <div
          data-testid="rfc-copilot-panel"
          className="fixed inset-y-4 right-4 z-50 flex w-[520px] max-w-[95vw] flex-col overflow-hidden rounded-2xl border border-[#c7d5db] bg-white shadow-2xl animate-fade-in"
        >
          {/* header */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#0f7fa1] to-[#0b5f78] px-4 py-3.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-white">Ask BGO</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#fbfcfd] px-4 py-4">
            {messages.length === 0 && (
              <div className="space-y-3">
                <MessageBubble
                  role="assistant"
                  content={"Hi, I'm BGO. Ask me anything about this proposal — the numbers, the details, the plan."}
                />
                <div className="flex flex-wrap gap-1.5 pl-8">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#c7d5db] bg-white px-2.5 py-1 text-[11px] font-medium text-[#435a68] transition hover:border-[#0f7fa1]/50 hover:text-[#0b5f78]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {loading && (
              <div className="flex items-end gap-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0f7fa1] to-[#0b5f78]">
                  <Bot className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="rounded-2xl rounded-bl-sm bg-[#eef2f4]">
                  <TypingDots />
                </div>
              </div>
            )}
          </div>

          {/* input */}
          <div className="flex items-end gap-2 border-t border-[#e3e9eb] bg-white p-3">
            <textarea
              data-testid="rfc-copilot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder="Ask a question…"
              className="max-h-24 flex-1 resize-none rounded-xl border border-[#c7d5db] bg-[#fbfcfd] px-3 py-2 text-[13.5px] text-[#0d1b26] placeholder-[#6f8592] outline-none transition focus:border-[#0f7fa1] focus:ring-2 focus:ring-[#0f7fa1]/20"
            />
            <button
              data-testid="rfc-copilot-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0f7fa1] text-white transition hover:bg-[#0b5f78] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {!open && (
        <button
          data-testid="rfc-copilot-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0f7fa1] to-[#0b5f78] py-3.5 pl-4 pr-5 text-sm font-bold text-white shadow-xl shadow-[#0f7fa1]/30 transition hover:shadow-[#0f7fa1]/50 hover:-translate-y-0.5"
        >
          <span className="relative flex h-5 w-5 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-white/30" />
            <Sparkles className="relative h-4 w-4" />
          </span>
          Ask BGO
        </button>
      )}
    </div>
  );
}
